import { FlatUsernameReport } from 'components/ChatBV/types';

import { normalizeArray, normalizeValue } from '../generalUtils';

const PLATFORM_X = 'x';
const PLATFORM_TWITTER = 'twitter';
const PLATFORM_X_DOMAIN = ['x', 'com'].join('.');

const isXTwitterPlatform = (platform: string): boolean => {
  const lowerPlatform = platform.toLowerCase();
  if (lowerPlatform.includes(PLATFORM_TWITTER)) {
    return true;
  }
  if (lowerPlatform === PLATFORM_X) {
    return true;
  }
  if (lowerPlatform === PLATFORM_X_DOMAIN) {
    return true;
  }
  return false;
};

export const mapUsernameReportToFlatSections = (
  reportData: any
): FlatUsernameReport => {
  const people = reportData?.entities?.people || [];

  const firstPerson = people[0];

  const allProfiles: any[] = [];
  const allUsernames: any[] = [];
  const usernamePresence: Record<string, boolean> = {};

  people.forEach((person: any) => {
    const profiles = person?.social?.profiles || [];
    const usernames = person?.social?.usernames || [];

    profiles.forEach((profile: any) => {
      const platform = profile?.platform?.toLowerCase() || '';
      if (platform.includes('instagram')) {
        usernamePresence.instagram = true;
      } else if (platform.includes('facebook')) {
        usernamePresence.facebook = true;
      } else if (isXTwitterPlatform(platform)) {
        usernamePresence['x/twitter'] = true;
      } else if (platform.includes('snapchat')) {
        usernamePresence.snapchat = true;
      } else if (platform.includes('reddit')) {
        usernamePresence.reddit = true;
      } else if (platform.includes('linkedin')) {
        usernamePresence.linkedin = true;
      } else if (platform.includes('youtube')) {
        usernamePresence.youtube = true;
      } else if (platform.includes('telegram')) {
        usernamePresence.telegram = true;
      } else if (platform.includes('tiktok')) {
        usernamePresence.tiktok = true;
      }

      allProfiles.push(profile);
    });

    usernames.forEach((usernameObj: any) => {
      allUsernames.push(usernameObj);
    });
  });

  return {
    permalink: normalizeValue(reportData?.meta?.permalink),

    // IDENTITY: Names, aliases, ages, DOBs from potential owners
    identity_or_owner_identity_info: firstPerson
      ? {
          full_name: normalizeValue(firstPerson?.identity?.names?.[0]?.full),
          first_name: normalizeValue(
            firstPerson?.identity?.names?.[0]?.parsed?.first
          ),
          middle_name: normalizeValue(
            firstPerson?.identity?.names?.[0]?.parsed?.middle
          ),
          last_name: normalizeValue(
            firstPerson?.identity?.names?.[0]?.parsed?.last
          ),
          all_names: normalizeArray(
            firstPerson?.identity?.names?.map((n: any) => n.full) || []
          ),
          ages: normalizeArray(firstPerson?.identity?.ages || []),
          dobs: normalizeArray(firstPerson?.identity?.dobs || []),
          dods: normalizeArray(firstPerson?.identity?.dods || []),
          demographic: {
            gender: normalizeValue(firstPerson?.demographic?.gender),
            ethnicities: normalizeArray(
              firstPerson?.demographic?.ethnicities || []
            ),
            origin_countries: normalizeArray(
              firstPerson?.demographic?.origin_countries || []
            ),
            languages: normalizeArray(
              firstPerson?.demographic?.languages || []
            ),
          },
        }
      : undefined,

    // SOCIAL: Social media profiles
    email_phone_or_social_info: {
      social: {
        profiles: normalizeArray(allProfiles),
        usernames: normalizeArray(allUsernames),
        user_ids: normalizeArray(
          people.flatMap((p: any) => p?.social?.user_ids || [])
        ),
      },
    },

    // USERNAME PRESENCE: Which platforms have this username
    username_presence: usernamePresence,

    // POTENTIAL OWNERS: Information about people associated with the username
    potential_owner: normalizeArray(
      people.slice(0, 10).map((person: any) => {
        // Build address, avoiding empty strings like ", " or ","
        let address = '';
        if (person?.contact?.addresses?.[0]?.full) {
          address = person.contact.addresses[0].full;
        } else {
          const city = person?.contact?.addresses?.[0]?.parsed?.city || '';
          const state = person?.contact?.addresses?.[0]?.parsed?.state || '';
          if (city || state) {
            address = [city, state].filter(Boolean).join(', ');
          }
        }

        return {
          name: normalizeValue(person?.identity?.names?.[0]?.full),
          age: normalizeValue(person?.identity?.ages?.[0]?.years),
          address: normalizeValue(address),
          phone: normalizeValue(person?.contact?.phones?.[0]?.phone),
          email: normalizeValue(person?.contact?.emails?.[0]?.address),
          jobs: normalizeArray(
            person?.jobs?.map((job: any) => job?.title) || []
          ),
          education: normalizeArray(
            person?.educations?.map((edu: any) => edu?.school) || []
          ),
          criminal: normalizeArray(
            person?.criminal_events?.events?.map((event: any) => event?.type) ||
              []
          ),
        };
      })
    ),
  };
};
